import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { getPaypalConfig } from "@/lib/api";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import ProductDetail from "@/pages/ProductDetail";
import Success from "@/pages/Success";
import About from "@/pages/About";
import Reviews from "@/pages/Reviews";
import Vault from "@/pages/Vault";
import QrPage from "@/pages/QrPage";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";
import Listen from "@/pages/Listen";
import Reader from "@/pages/Reader";
import ReaderGift from "@/pages/ReaderGift";
import ReaderReset from "@/pages/ReaderReset";
import Account from "@/pages/Account";
import AccountVerify from "@/pages/AccountVerify";
import GiftCardPage from "@/pages/GiftCardPage";
import AdminBooks from "@/pages/AdminBooks";
import ReaderBook from "@/pages/ReaderBook";
import ReaderAuthCallback from "@/components/reader/ReaderAuthCallback";

function AppRoutes() {
  const location = useLocation();
  // Google sign-in returns with #session_id=...; handle it before any route renders
  if (location.hash?.includes("session_id=")) return <ReaderAuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/books/:id" element={<ProductDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/reader" element={<Reader />} />
        <Route path="/reader/gift" element={<ReaderGift />} />
        <Route path="/listen" element={<Listen />} />
        <Route path="/reader/reset" element={<ReaderReset />} />
        <Route path="/account" element={<Account />} />
        <Route path="/account/verify" element={<AccountVerify />} />
        <Route path="/gift-card/:token" element={<GiftCardPage />} />
        <Route path="/success" element={<Success />} />
        <Route path="/admin/books" element={<AdminBooks />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* Hidden routes (no nav chrome) */}
      <Route path="/reader/read/:bookId" element={<ReaderBook />} />
      <Route path="/vault/:key" element={<Vault />} />
      <Route path="/qr" element={<QrPage />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

function App() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    getPaypalConfig()
      .then((c) => setCfg(c))
      .catch(() => setCfg({ client_id: "", currency: "AUD" }));
  }, []);

  if (!cfg) {
    return (
      <div className="App grain min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-gold animate-spin" />
      </div>
    );
  }

  const paypalOptions = {
    "client-id": cfg.client_id || "",
    currency: cfg.currency || "AUD",
    components: "buttons",
    "disable-funding": "venmo,paylater",
    intent: "capture",
  };

  return (
    <div className="App grain">
      <Toaster theme="dark" position="top-center" richColors />
      <PayPalScriptProvider options={paypalOptions} deferLoading={!cfg.client_id}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </PayPalScriptProvider>
    </div>
  );
}

export default App;
